import React, { useState } from 'react';
import ReactReadMoreReadLess from 'react-read-more-read-less';
import { Collapse, OverlayTrigger, Popover } from 'react-bootstrap';
import { KTSVG } from '../../../_metronic/helpers';
import CurrencyComp from '../../../components/currencyComponent/CurrencyComp';
import DateTimeComp from '../../../components/dateComponent/DateTimeComp';
import { FaEnvelope, FaFileAlt, FaChevronDown, FaChevronUp } from 'react-icons/fa';

interface InvoiceDraftDetailProps {
    data: any;
    isLoading?: boolean;
    onClose?: () => void;
    onImageClick?: (url: string) => void;
    onRetry?: () => void;
}

const isMsgOrEml = (fileURL: string): boolean => {
    if (!fileURL) return false;
    const lower = fileURL.toLowerCase();
    return lower.endsWith('.msg') || lower.endsWith('.eml');
};

const isHeicFile = (fileURL: string): boolean => {
    if (!fileURL) return false;
    const lower = fileURL.toLowerCase();
    return lower.endsWith('.heic') || lower.endsWith('.heif');
};

const getFileExtension = (fileURL: string): string => {
    if (!fileURL) return 'File';
    const ext = fileURL.toLowerCase().split('.').pop();
    return ext?.toUpperCase() || 'File';
};

const getFileNameFromURL = (fileURL: string): string => {
    if (!fileURL) return 'File';
    const parts = fileURL.split('/');
    return parts[parts.length - 1] || 'File';
};

const InvoiceDraftDetail: React.FC<InvoiceDraftDetailProps> = ({
    data,
    isLoading,
    onClose,
    onImageClick,
    onRetry,
}) => {
    const [isItemDetailsOpen, setIsItemDetailsOpen] = useState(true);
    const [isReportDetailsOpen, setIsReportDetailsOpen] = useState(true);
    if (isLoading) {
        return (
            <div className="card h-100">
                <div className="card-body d-flex align-items-center justify-content-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="card h-100">
                <div className="card-body d-flex align-items-center justify-content-center">
                    <p className="text-muted">Select an invoice to view details</p>
                </div>
            </div>
        );
    }

    const getStatusBadge = (status: number) => {
        const statusMap: { [key: number]: { label: string; className: string } } = {
            0: { label: 'Processing', className: 'badge-light-warning' },
            1: { label: 'Ready To Use', className: 'badge-light-success' },
            2: { label: 'Failed', className: 'badge-light-danger' },
            3: { label: 'Attached', className: 'badge-light-primary' },
            4: { label: 'Submitted', className: 'badge-info' },
            5: { label: 'Already Used', className: 'badge-secondary' },
        };
        const info = statusMap[status] || { label: 'Unknown', className: 'badge-light-dark' };
        return <span className={`badge ${info.className} fs-7 fw-semibold`}>{info.label}</span>;
    };

    const getRecordSource = (sourceId?: number) => {
        const sources: { [key: number]: string } = {
            1: 'Web App',
            2: 'Mobile App',
            3: 'Bank Feed',
        };
        return sources[sourceId ?? 0] || 'N/A';
    };

    const isValidDate = (dateStr?: string) => {
        if (!dateStr) return false;
        return dateStr !== '0001-01-01T00:00:00';
    };

    // Render label + value in a row, exactly like the grid card style
    const renderField = (
        label: string,
        value: React.ReactNode,
        colWidth: number = 6,
        isMultiline: boolean = false
    ) => {
        return (
            <div className={`col-${colWidth} mb-1`}>
                <div className={`d-flex ${isMultiline ? 'align-items-start flex-wrap' : 'align-items-center'}`}>
                    <h5 className="card-title fs-8 fw-bold m-0 w-100px flex-shrink-0">
                        {label}:
                    </h5>
                    <p
                        className={`mb-0 fs-7 ${isMultiline ? 'text-wrap flex-grow-1' : 'text-truncate'}`}
                        style={isMultiline ? { wordBreak: 'break-word' } : undefined}
                    >
                        {value || 'N/A'}
                    </p>
                </div>
            </div>
        );
    };

    const renderFilePreview = (fileURL: string) => {
        const isMsgOrEmlFile = isMsgOrEml(fileURL);
        const isHeic = isHeicFile(fileURL);
        const fileName = getFileNameFromURL(fileURL);
        const ext = getFileExtension(fileURL);

        if (fileURL?.toLowerCase().endsWith('.pdf')) {
            return (
                <div
                    className="position-relative w-100"
                    style={{ height: '200px', cursor: 'pointer' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onImageClick?.(fileURL);
                    }}
                >
                    <embed
                        src={`${fileURL}#toolbar=0&navpanes=0&scrollbar=0`}
                        type="application/pdf"
                        width="100%"
                        height="200px"
                        className="cursor-pointer"
                    />
                </div>
            );
        }

        if (isMsgOrEmlFile) {
            return (
                <div
                    className="d-flex flex-column align-items-center justify-content-center p-3"
                    style={{ height: '200px', backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0', cursor: 'pointer' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onImageClick?.(fileURL);
                    }}
                >
                    <FaEnvelope className="text-primary" style={{ fontSize: '48px' }} />
                    <h6 className="mt-3 text-center px-2 w-100 text-truncate">{fileName}</h6>
                    <span className="badge bg-primary mt-2">{ext}</span>
                    <p className="text-muted mt-2 mb-0"><small>Click to view</small></p>
                </div>
            );
        }

        if (isHeic) {
            return (
                <div
                    className="d-flex flex-column align-items-center justify-content-center p-3"
                    style={{ height: '200px', backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0', cursor: 'pointer' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onImageClick?.(fileURL);
                    }}
                >
                    <FaFileAlt className="text-warning" style={{ fontSize: '48px' }} />
                    <h6 className="mt-3 text-center px-2 w-100 text-truncate">{fileName}</h6>
                    <span className="badge bg-warning mt-2">{ext}</span>
                    <p className="text-muted mt-2 mb-0"><small>Click to view</small></p>
                </div>
            );
        }

        return (
            <img
                className="w-100 cursor-pointer"
                src={fileURL}
                alt="Receipt preview"
                style={{ height: '200px', objectFit: 'contain', cursor: 'pointer' }}
                onClick={(e) => {
                    e.stopPropagation();
                    onImageClick?.(fileURL);
                }}
                onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = 'media/svg/files/blank-image.svg';
                }}
            />
        );
    };

    return (
        <div className="card h-100">
            <div className="card-header border-0 pt-5 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                    <h5 className="card-title fw-bold mb-0">Receipt Details</h5>
                    {data?.ocrReadStatus === 2 && data?.noOfRetry <= 2 && onRetry && (
                        <button
                            type="button"
                            className="btn btn-sm rounded-pill"
                            style={{
                                padding: '0.35rem 0.8rem',
                                fontSize: '0.75rem',
                                color: '#856404',
                                backgroundColor: 'transparent',
                                border: '1px solid #ffc107',
                            }}
                            onClick={onRetry}
                        >
                            Retry ({data.noOfRetry})
                        </button>
                    )}
                </div>
                {onClose && (
                    <button className="btn btn-sm btn-icon btn-light-danger" onClick={onClose}>
                        <KTSVG path="/media/icons/duotune/arrows/arr088.svg" className="svg-icon-2" />
                    </button>
                )}
            </div>

            <div className="card-body pt-0">
                <div className="mb-3 pb-3">
                    <div className="row">
                        {renderField('Status', getStatusBadge(data.ocrReadStatus))}
                        {renderField('Uploaded From', getRecordSource(data.recordSourceID))}
                        {renderField('Retry Count', data.noOfRetry ?? 0)}
                        {renderField('Uploaded Date', data.createdDT ? <DateTimeComp formattedDate={data.createdDT} /> : 'N/A')}
                    </div>
                </div>
                {data.fileURL ? (
                    <div className="mb-3">{renderFilePreview(data.fileURL)}</div>
                ) : (
                    <div className="symbol symbol-80px symbol-circle bg-light-primary mx-auto mb-3">
                        <KTSVG path="/media/icons/duotune/files/fil014.svg" className="svg-icon-3x text-primary" />
                    </div>
                )}

                {data.remarks && (
                    <div className="alert alert-warning d-flex align-items-center p-3 mb-3" role="alert">
                        <KTSVG path="/media/icons/duotune/general/gen044.svg" className="svg-icon-2 me-2" />
                        <span className="fs-7">{data.remarks}</span>
                    </div>
                )}

                <div className="border-bottom mb-3 pb-3">
                    <div className="row">
                        {renderField('Invoice No', data.invoiceNo)}
                        {renderField('Vendor', data.vendorName)}
                        {renderField('Amount', data.amount ? <CurrencyComp amount={data.amount} /> : '0.00')}
                        {renderField('Payment Method', data.expensePayment?.title)}
                        {renderField('Invoice ID', data.invoiceId)}
                        {renderField('Receipt Date', isValidDate(data.invoiceDate) ? <DateTimeComp formattedDate={data.invoiceDate} /> : 'N/A', 6)}
                        {renderField(
                            'Description',
                            data.readDocShortDesc ? (
                                <div className="w-100">
                                    <ReactReadMoreReadLess
                                        charLimit={50}
                                        readMoreText="Read more"
                                        readLessText="Read less"
                                        readMoreClassName="readMore"
                                        readLessClassName="readLess"
                                    >
                                        {data.readDocShortDesc}
                                    </ReactReadMoreReadLess>
                                </div>
                            ) : (
                                'N/A'
                            ),
                            12,
                            true
                        )}
                    </div>
                </div>

                <div className="border-bottom mb-3 pb-3">
                    <h6 className="fs-7 fw-bold mb-2 text-primary">👤 User Details</h6>
                    <div className="row">
                        {renderField('SS User ID', data.orgUserId)}
                        {renderField('User Name', data.orgUserName)}
                    </div>
                </div>

                <div className="border-bottom mb-3 pb-3">
                    <button
                        className="btn btn-sm btn-link text-start p-0 mb-2 d-flex justify-content-between align-items-center w-100"
                        style={{ fontWeight: 600 }}
                        onClick={() => setIsItemDetailsOpen((prev) => !prev)}
                    >
                        <span className="fs-7 text-primary">🧾 SS Expense Item Details</span>
                        {isItemDetailsOpen ? (
                            <FaChevronUp className="text-primary" />
                        ) : (
                            <FaChevronDown className="text-primary" />
                        )}
                    </button>
                    <Collapse in={isItemDetailsOpen}>
                        <div>
                            <div className="row">
                                {renderField('Expense Item ID', data.expenseItemId)}
                                {renderField('Expense Category', data.category)}
                                {renderField('Payment Method', data.expenseItemLocation?.title)}
                                {renderField('Expense Type', data.expenseType?.title)}
                                {renderField('Charge Location', data.expenseItemLocation?.title)}
                                {renderField('Vendor', data.itemVendorName)}
                                {renderField('Item Date', isValidDate(data.invoiceDate) ? <DateTimeComp formattedDate={data.invoiceDate} /> : 'N/A', 6)}
                                {renderField('Item Amount', data.expenseItemAmount ? <CurrencyComp amount={data.expenseItemAmount} /> : 'N/A')}
                                {renderField('Item Source', data.expenseItemAmount ? <CurrencyComp amount={data.expenseItemAmount} /> : 'N/A')}
                                {renderField('Item Created Date', isValidDate(data.expenseItemCreatedDate) ? <DateTimeComp formattedDate={data.expenseItemCreatedDate} /> : 'N/A', 6)}
                            </div>
                        </div>
                    </Collapse>
                </div>

                <div className="border-bottom mb-3 pb-3">
                    <button
                        className="btn btn-sm btn-link text-start p-0 mb-2 d-flex justify-content-between align-items-center w-100"
                        style={{ fontWeight: 600 }}
                        onClick={() => setIsReportDetailsOpen((prev) => !prev)}
                    >
                        <span className="fs-7 text-primary">📊 SS Expense Report Details</span>
                        {isReportDetailsOpen ? (
                            <FaChevronUp className="text-primary" />
                        ) : (
                            <FaChevronDown className="text-primary" />
                        )}
                    </button>
                    <Collapse in={isReportDetailsOpen}>
                        <div>
                            <div className="row">
                                {renderField('Tracking ID', <span className="text-primary">{data.trackingId}</span>)}
                                {renderField('Report Status', data.expenseStatus?.title && <span className="badge badge-light-success">{data.expenseStatus.title}</span>)}
                                {renderField('Report Date', data.reportDate ? <DateTimeComp formattedDate={data.reportDate} /> : 'N/A')}
                                {renderField('Report Amount', data.reportAmount ? <CurrencyComp amount={data.reportAmount} /> : '0.00')}
                                {renderField('Report Title', data.reportTitle)}
                            </div>
                        </div>
                    </Collapse>
                </div>

                <div>
                    <h6 className="fs-7 fw-bold mb-2 text-primary">🤖 OCR Details</h6>
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                        {data.fileURL && (
                            <button
                                className="btn btn-sm btn-light-primary"
                                onClick={() => onImageClick?.(data.fileURL)}
                            >
                                View Report
                            </button>
                        )}
                        {data.fileURL && (
                            <button
                                className="btn btn-sm btn-light-primary"
                                onClick={() => onImageClick?.(data.fileURL)}
                            >
                                View LineItem
                            </button>
                        )}
                        {data.fileURL && (
                            <button
                                className="btn btn-sm btn-light-primary"
                                onClick={() => onImageClick?.(data.fileURL)}
                            >
                                View Receipt
                            </button>
                        )}
                        <OverlayTrigger
                            trigger="click"
                            rootClose
                            placement="bottom"
                            overlay={
                                <Popover id="ocr-text-popover">
                                    <Popover.Header as="h3">OCR Text</Popover.Header>
                                    <Popover.Body style={{ maxWidth: '400px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {data.ocrTextData || 'No OCR text available.'}
                                    </Popover.Body>
                                </Popover>
                            }
                        >
                            <button className="btn btn-sm btn-light-primary">
                                View OCR Text
                            </button>
                        </OverlayTrigger>
                        <div className="d-flex align-items-center gap-2">
                            <span className="fs-7 fw-semibold" style={{ color: '#5e6278' }}>Confidence Score:</span>
                            <span className="fw-bold text-success">98%</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvoiceDraftDetail;